<template>
  <div class="visualizer-header">
    <div class="visualizer-header__left">
      <div class="visualizer-title">
        {{ $t('domain.insights.cve.attack.title') }}
      </div>
      <div class="visualizer-subtitle">
        {{ dynamicSubtitle }}
      </div>
    </div>
    <div v-if="availableCves.length > 0" class="visualizer-header__right">
      <!-- Path Mode Selector (Chip Dropdown) -->
      <v-menu offset-y :close-on-content-click="true" min-width="160">
        <template v-slot:activator="{ on, attrs }">
          <div class="path-mode-chip" v-bind="attrs" v-on="on">
            <v-icon small class="path-mode-chip__icon">{{ pathModeOptions[pathMode].icon }}</v-icon>
            <span class="path-mode-chip__label">{{ pathModeOptions[pathMode].label }}</span>
            <v-icon small class="path-mode-chip__chevron">mdi-chevron-down</v-icon>
          </div>
        </template>
        <v-card class="path-mode-menu">
          <v-list dense class="path-mode-list">
            <v-list-item
              v-for="(option, key) in pathModeOptions"
              :key="key"
              class="path-mode-item"
              :class="{ 'path-mode-item--active': pathMode === key }"
              @click="$emit('update:pathMode', key)"
            >
              <v-list-item-icon class="path-mode-item__icon-wrapper">
                <v-icon small :color="pathMode === key ? 'primary' : ''">{{ option.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="path-mode-item__title">{{ option.label }}</v-list-item-title>
                <v-list-item-subtitle class="path-mode-item__path">{{ option.path }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="pathMode === key" class="path-mode-item__check">
                <v-icon small color="primary">mdi-check</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>

      <!-- CVE Selector -->
      <v-menu
        v-model="menuOpen"
        :close-on-content-click="false"
        offset-y
        max-width="320"
        min-width="280"
      >
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on" class="cve-selector-wrapper">
            <v-text-field
              :value="selectorDisplayText"
              readonly
              dense
              outlined
              hide-details
              class="cve-selector-input"
              append-icon="mdi-chevron-down"
            />
          </div>
        </template>
        <v-card class="cve-dropdown">
          <div class="cve-dropdown__search-wrapper">
            <v-icon small color="grey">mdi-magnify</v-icon>
            <input
              v-model="localSearchQuery"
              type="text"
              :placeholder="$t('domain.insights.cve.attack.search.placeholder')"
              class="cve-dropdown__search-input"
            />
            <v-icon
              v-if="localSearchQuery"
              small
              color="grey"
              class="cve-dropdown__clear-search"
              @click="localSearchQuery = ''"
            >
              mdi-close
            </v-icon>
          </div>
          <div class="cve-dropdown__actions">
            <v-btn
              x-small
              text
              color="primary"
              :disabled="allSelected"
              @click="$emit('select-all')"
            >
              <v-icon x-small left>mdi-checkbox-multiple-marked</v-icon>
              {{ $t('domain.insights.cve.attack.select.all') }}
            </v-btn>
            <v-btn
              x-small
              text
              color="error"
              :disabled="selectedCves.length === 0"
              @click="$emit('clear-selection')"
            >
              <v-icon x-small left>mdi-close-circle</v-icon>
              {{ $t('domain.insights.cve.attack.clear') }}
            </v-btn>
          </div>
          <v-divider />
          <v-list dense class="cve-dropdown__list">
            <v-list-item
              v-for="cve in filteredCves"
              :key="cve"
              class="cve-dropdown__item"
              @click="$emit('toggle-cve', cve)"
            >
              <v-list-item-action class="mr-2">
                <v-checkbox
                  :input-value="selectedCves.includes(cve)"
                  color="primary"
                  dense
                  hide-details
                  @click.stop="$emit('toggle-cve', cve)"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title class="cve-dropdown__cve-text">
                  {{ cve }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="filteredCves.length === 0">
              <v-list-item-content>
                <v-list-item-title class="text-center grey--text">
                  No CVEs found
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-divider />
          <div class="cve-dropdown__footer">
            <span class="cve-dropdown__count">
              <v-icon x-small color="primary" class="mr-1">mdi-check-circle</v-icon>
              {{ selectedCves.length }} / {{ availableCves.length }} selected
            </span>
            <v-btn small color="primary" depressed @click="menuOpen = false">
              Done
            </v-btn>
          </div>
        </v-card>
      </v-menu>

      <!-- Filters -->
      <c-v-e-filters-menu
        :value="filters"
        :apt-groups-list="aptGroupsList"
        @input="$emit('update:filters', $event)"
      />

      <!-- Export Menu -->
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            small
            outlined
            :disabled="!hasData"
            class="export-btn"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon small left>mdi-download</v-icon>
            Export
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="$emit('export', 'json')">
            <v-list-item-icon class="mr-2">
              <v-icon small>mdi-code-json</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Export as JSON</v-list-item-title>
              <v-list-item-subtitle>Raw data format</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item disabled>
            <v-list-item-icon class="mr-2">
              <v-icon small>mdi-file-document-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Export as HTML</v-list-item-title>
              <v-list-item-subtitle>Coming soon</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- Fullscreen Button -->
      <v-btn
        small
        outlined
        :disabled="!hasData"
        class="fullscreen-btn"
        @click="$emit('fullscreen')"
      >
        <v-icon small>mdi-fullscreen</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import CVEFiltersMenu from './CVEFiltersMenu.vue';
import { PATH_MODE_OPTIONS } from '../shared';

export default {
  name: 'CVEVisualizerHeader',
  components: {
    CVEFiltersMenu,
  },
  props: {
    availableCves: {
      type: Array,
      default: () => [],
    },
    selectedCves: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({ aptGroups: [] }),
    },
    aptGroupsList: {
      type: Array,
      default: () => [],
    },
    hasData: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    pathMode: {
      type: String,
      default: 'full',
    },
  },
  data() {
    return {
      menuOpen: false,
      localSearchQuery: '',
    };
  },
  computed: {
    pathModeOptions() {
      return PATH_MODE_OPTIONS;
    },
    dynamicSubtitle() {
      return this.pathModeOptions[this.pathMode]?.path || this.pathModeOptions.full.path;
    },
    selectorDisplayText() {
      if (this.selectedCves.length === 0) {
        return 'Select CVEs...';
      }
      if (this.selectedCves.length === 1) {
        return this.selectedCves[0];
      }
      return `${this.selectedCves.length} CVEs selected`;
    },
    allSelected() {
      return this.selectedCves.length === this.availableCves.length;
    },
    filteredCves() {
      if (!this.localSearchQuery) {
        return this.availableCves;
      }
      const query = this.localSearchQuery.toLowerCase();
      return this.availableCves.filter(cve => cve.toLowerCase().includes(query));
    },
  },
  watch: {
    localSearchQuery(val) {
      this.$emit('update:searchQuery', val);
    },
    searchQuery(val) {
      this.localSearchQuery = val;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.visualizer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $space-4;
  flex-wrap: wrap;
  gap: $space-3;

  &__left {
    .visualizer-title {
      font-size: $font-size-md;
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: $space-1;
    }

    .visualizer-subtitle {
      font-size: $font-size-sm;
      color: var(--text-secondary);
    }
  }

  &__right {
    display: flex;
    align-items: center;
    gap: $space-2;
    flex-wrap: wrap;

    .cve-selector-wrapper {
      cursor: pointer;
      width: 200px;
      flex-shrink: 0;
      height: $space-9;
    }

    .export-btn,
    .fullscreen-btn {
      flex-shrink: 0;
      height: $space-9;
    }
  }
}

// Path Mode Chip Dropdown
.path-mode-chip {
  display: flex;
  align-items: center;
  gap: $space-1;
  padding: $space-1 $space-2;
  background: var(--bg-secondary);
  border: $border-width-thin solid var(--border-primary);
  border-radius: $radius-full;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--brand-primary);
  }

  &__icon {
    color: var(--brand-primary);
    font-size: $font-size-sm;
  }

  &__label {
    font-size: $font-size-xs;
    font-weight: 500;
    color: var(--text-primary);
    white-space: nowrap;
  }

  &__chevron {
    color: var(--text-muted);
    font-size: $font-size-sm;
    transition: transform 0.2s ease;
  }
}

.path-mode-menu {
  border-radius: $radius-md;
  overflow: hidden;
  box-shadow: $shadow-md;
}

.path-mode-list {
  padding: $space-1 0;
  background: var(--bg-widget);
}

.path-mode-item {
  padding: $space-1 $space-3;
  min-height: 36px;
  transition: background 0.15s ease;

  &:hover {
    background: var(--highlight-blue-bg);
  }

  &--active {
    background: var(--highlight-blue-bg);
  }

  &__icon-wrapper {
    margin-right: $space-2;
    min-width: 20px;
  }

  &__title {
    font-size: $font-size-xs;
    font-weight: 500;
    color: var(--text-primary);
    line-height: 1.2;
  }

  &__path {
    font-size: 10px;
    color: var(--text-muted);
    line-height: 1.2;
  }

  &__check {
    margin-left: $space-2;
    min-width: auto;
  }
}

.cve-dropdown {
  border-radius: $radius-lg;
  overflow: hidden;
  box-shadow: $shadow-lg;

  &__search-wrapper {
    display: flex;
    align-items: center;
    gap: $space-2;
    padding: $space-3;
    background: var(--bg-secondary);
    border-bottom: $border-width-thin solid var(--border-primary);
  }

  &__search-input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: $font-size-base;
    color: var(--text-primary);
    outline: none;

    &::placeholder {
      color: var(--text-muted);
    }
  }

  &__clear-search {
    cursor: pointer;
    opacity: 0.6;

    &:hover {
      opacity: 1;
    }
  }

  &__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $space-2 $space-3;
    background: var(--bg-secondary);
    border-bottom: $border-width-thin solid var(--border-primary);
  }

  &__list {
    max-height: 240px;
    overflow-y: auto;
    padding: $space-1 0;
  }

  &__item {
    min-height: $space-10;
    padding: 0 $space-3;
    transition: background $transition-duration-fast ease;

    &:hover {
      background: var(--highlight-blue-bg);
    }
  }

  &__cve-text {
    font-size: $font-size-base - 0.0625rem;
    font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
    color: var(--text-primary);
    font-weight: 500;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $space-2 $space-3;
    background: var(--bg-secondary);
    border-top: $border-width-thin solid var(--border-primary);
  }

  &__count {
    font-size: $font-size-sm;
    color: var(--text-muted);
    font-weight: 500;
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

#app .cve-selector-input.v-text-field {
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  pointer-events: none;

  .v-input__control {
    min-height: $space-9;
    height: $space-9;
  }

  .v-input__slot {
    min-height: $space-9;
    height: $space-9;
    margin-bottom: 0;
  }

  .v-text-field__details {
    display: none;
  }

  fieldset {
    height: $space-10;
  }
}

// Dark mode styles for dropdowns
.dark-theme #app .cve-dropdown,
#app .dark-theme .cve-dropdown {
  background: var(--bg-secondary);

  .v-list {
    background: var(--bg-secondary);
  }

  .v-list-item {
    color: var(--text-primary);

    &:hover {
      background: var(--highlight-blue-bg);
    }
  }

  .v-list-item__title {
    color: var(--text-primary);
  }

  .v-list-item__content {
    color: var(--text-primary);
  }
}

.dark-theme #app .path-mode-menu,
#app .dark-theme .path-mode-menu {
  background: var(--bg-secondary);

  .v-list {
    background: var(--bg-secondary);
  }

  .v-list-item {
    color: var(--text-primary);

    &:hover {
      background: var(--highlight-blue-bg);
    }
  }

  .v-list-item__title {
    color: var(--text-primary);
  }

  .v-list-item__subtitle {
    color: var(--text-muted);
  }
}

// Export menu dark mode
.dark-theme #app .v-menu__content .v-list,
#app .dark-theme .v-menu__content .v-list {
  background: var(--bg-secondary);

  .v-list-item {
    color: var(--text-primary);

    &:hover {
      background: var(--highlight-blue-bg);
    }
  }

  .v-list-item__title {
    color: var(--text-primary);
  }

  .v-list-item__subtitle {
    color: var(--text-muted);
  }

  .v-list-item__icon .v-icon {
    color: var(--text-secondary);
  }
}
</style>
